import { useCallback, useEffect, useMemo, useState } from 'react'
import { getWallets } from '@wallet-standard/core'
import { SuiWalletAdapter } from '../controller/AccountController/BaseAccountController'

const SUI_FEATURES = {
  signAndExecute: 'sui:signAndExecuteTransaction',
  signAndExecuteBlock: 'sui:signAndExecuteTransactionBlock',
  signTransaction: 'sui:signTransaction',
  signTransactionBlock: 'sui:signTransactionBlock',
}

const STANDARD_FEATURES = {
  connect: 'standard:connect',
  disconnect: 'standard:disconnect',
}

type WalletStandardWallet = {
  name: string
  icon?: string
  accounts?: Array<{ address: string; chains?: string[] }>
  features?: Record<string, any>
}

type SuiWalletItem = WalletStandardWallet

const isSuiWallet = (wallet: WalletStandardWallet) =>
  Boolean(
    wallet?.features?.[SUI_FEATURES.signAndExecute] ||
      wallet?.features?.[SUI_FEATURES.signAndExecuteBlock],
  )

const getWalletsApi = () => getWallets() as any

export const useSuiWallet = () => {
  const [wallets, setWallets] = useState<SuiWalletItem[]>([])
  const [currentWallet, setCurrentWallet] = useState<SuiWalletItem | null>(null)
  const [publicKey, setPublicKey] = useState<string | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)

  useEffect(() => {
    const walletsApi = getWalletsApi()
    const updateWallets = () => {
      const allWallets = walletsApi?.get ? walletsApi.get() : []
      const suiWallets = (allWallets || []).filter(isSuiWallet)
      setWallets(suiWallets)
    }

    updateWallets()

    if (typeof walletsApi?.subscribe === 'function' || typeof walletsApi?.on === 'function') {
      const unsubscribe = walletsApi.subscribe
        ? walletsApi.subscribe(updateWallets)
        : walletsApi.on('register', updateWallets)
      return () => {
        if (typeof unsubscribe === 'function') {
          unsubscribe()
        } else if (walletsApi?.off) {
          walletsApi.off('register', updateWallets)
        }
      }
    }
  }, [])

  useEffect(() => {
    if (!currentWallet) {
      setPublicKey(null)
      setIsConnected(false)
      return
    }
    const address = currentWallet?.accounts?.[0]?.address || null
    setPublicKey(address)
    setIsConnected(!!address)
  }, [currentWallet])

  const connect = useCallback(
    async (wallet?: SuiWalletItem) => {
      const targetWallet = wallet || wallets[0]
      if (!targetWallet) {
        throw new Error('Sui wallet not found')
      }
      setIsConnecting(true)
      try {
        const connectFeature = targetWallet.features?.[STANDARD_FEATURES.connect]
        if (connectFeature?.connect) {
          await connectFeature.connect()
        }
        const account = targetWallet.accounts?.[0]
        const address = account?.address || null
        setCurrentWallet(targetWallet)
        setPublicKey(address)
        setIsConnected(!!address)
        return address
      } finally {
        setIsConnecting(false)
      }
    },
    [wallets],
  )

  const disconnect = useCallback(async () => {
    if (!currentWallet) {
      setPublicKey(null)
      setIsConnected(false)
      return
    }
    try {
      const disconnectFeature = currentWallet.features?.[STANDARD_FEATURES.disconnect]
      if (disconnectFeature?.disconnect) {
        await disconnectFeature.disconnect()
      }
    } finally {
      setCurrentWallet(null)
      setPublicKey(null)
      setIsConnected(false)
    }
  }, [currentWallet])

  const walletAdapter = useMemo<SuiWalletAdapter | null>(() => {
    if (!currentWallet) {
      return null
    }
    return {
      accounts: currentWallet.accounts || [],
      signAndExecuteTransaction: currentWallet.features?.[SUI_FEATURES.signAndExecute]
        ?.signAndExecuteTransaction,
      signAndExecuteTransactionBlock: currentWallet.features?.[SUI_FEATURES.signAndExecuteBlock]
        ?.signAndExecuteTransactionBlock,
      signTransaction: currentWallet.features?.[SUI_FEATURES.signTransaction]?.signTransaction,
      signTransactionBlock: currentWallet.features?.[SUI_FEATURES.signTransactionBlock]
        ?.signTransactionBlock,
    }
  }, [currentWallet])

  return {
    wallets,
    isAvailable: wallets.length > 0,
    isConnected,
    isConnecting,
    publicKey,
    connect,
    disconnect,
    walletAdapter,
  }
}
